"use client";

// `"use client"` here is forced by the close button (`onClick`).
// Markup itself stays presentational, no state, no effects.

type FloatingHeaderProps = {
  readonly stepIndex?: number;
  readonly stepTotal?: number;
  readonly kicker: string;
  readonly titleId?: string;
  /**
   * Pinned to the right of the kicker.
   *
   * The header is the only horizontal room this panel has to spare — the
   * kicker rarely fills half of it — so a standing fact that must be
   * readable without being the subject of the screen belongs here rather
   * than costing a row of the body. The tempo of a run in time is exactly
   * that.
   */
  readonly trailing?: React.ReactNode;
  readonly onClose?: () => void;
};

const DEFAULT_TOTAL = 7;

export function FloatingHeader({
  stepIndex,
  stepTotal = DEFAULT_TOTAL,
  kicker,
  titleId,
  trailing,
  onClose,
}: FloatingHeaderProps) {
  const showStepCounter = stepIndex !== undefined;

  return (
    <header
      className="flex items-center gap-3 px-4 py-3"
      style={{ borderBottom: "1px solid var(--color-border-subtle)" }}
    >
      {showStepCounter && (
        <>
          <span
            className="font-mono text-[11px] tabular-nums"
            style={{ color: "var(--color-violet-text)" }}
          >
            {stepIndex}/{stepTotal}
          </span>

          <span
            aria-hidden
            style={{
              width: 1,
              height: 12,
              background: "var(--color-border-subtle)",
            }}
          />
        </>
      )}

      <h2
        id={titleId}
        className="flex-1 font-mono text-[11px] uppercase truncate"
        style={{
          color: "var(--color-text-secondary)",
          letterSpacing: "0.18em",
        }}
      >
        {kicker}
      </h2>

      {trailing}

      {onClose !== undefined && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="grid h-6 w-6 place-items-center rounded-md text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary focus:outline-none focus-visible:ring-2"
          style={{
            // Tailwind v4 ring-* via CSS var (avoid JIT class for inline ring color)
            ["--tw-ring-color" as string]: "var(--color-accent)",
          }}
        >
          <span aria-hidden className="text-[14px] leading-none">
            ×
          </span>
        </button>
      )}
    </header>
  );
}
